import { useState, useEffect, useRef, useCallback } from 'react'
import Peer from 'peerjs'
import { supabase } from '@/lib/supabase.js'

/**
 * Exact same flow as the meet app:
 *
 * CREATOR (host):
 *   1. Get stream
 *   2. new Peer()
 *   3. peer.on('open') → UPDATE rooms SET creator_peer_id = peerId
 *   4. peer.on('call') → call.answer(stream)
 *
 * GUEST:
 *   1. Fetch creator_peer_id from rooms table FIRST (before creating Peer)
 *   2. Get stream
 *   3. new Peer()
 *   4. peer.on('open') → peer.call(creatorPeerId, stream) immediately
 *
 * No realtime fallback for peer ID — meet doesn't use one either.
 * The guest fetches upfront; if the creator hasn't opened yet the guest
 * just waits on the loading screen until they re-enter (same as meet).
 *
 * Supabase channel is kept only as a signaling fallback in case the guest
 * loads before the creator peer opens (the one case meet handles via its
 * "room not found" guard — we handle it with a realtime listener instead).
 */

const PEERJS_CONFIG = {
  config: {
    iceServers: [
    {
      urls: 'stun:stun.l.google.com:19302'
    },
    {
      urls: 'turn:93.127.133.55:3478',
      username: 'test',
      credential: 'test123'
    }
  ]
  },
}

export function useVideo(roomId, isCreator) {
  const [localStream, setLocalStream]     = useState(null)
  const [remoteStream, setRemoteStream]   = useState(null)
  const [callConnected, setCallConnected] = useState(false)
  const [mediaError, setMediaError]       = useState(null)
  const [isMuted, setIsMuted]             = useState(false)
  const [isCameraOff, setIsCameraOff]     = useState(false)
  const [connectionStatus, setConnectionStatus] = useState('idle')

  const peerRef         = useRef(null)
  const localStreamRef  = useRef(null)
  const currentCallRef  = useRef(null)
  const channelRef      = useRef(null)
  const mountedRef      = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    if (!roomId) return
    let peer = null
    let stream = null

    async function init() {
      setConnectionStatus('connecting')

      // ── GUEST: fetch creator peer ID from rooms BEFORE creating Peer ──────
      // This is meet's exact pattern. The peer ID is already known by the time
      // peer.on('open') fires, so the call is placed with zero delay.
      let creatorPeerId = null
      if (!isCreator) {
        const { data } = await supabase
          .from('rooms')
          .select('creator_peer_id')
          .eq('id', roomId)
          .single()

        if (!mountedRef.current) return

        if (data?.creator_peer_id) {
          creatorPeerId = data.creator_peer_id
          console.log('[PeerJS] Fetched creator_peer_id upfront:', creatorPeerId)
        } else {
          console.log('[PeerJS] creator_peer_id not set yet — will catch via Realtime')
        }
      }

      // ── get stream ────────────────────────────────────────────────────────
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      } catch (err) {
        if (!mountedRef.current) return
        const msg =
          err.name === 'NotAllowedError' ? 'Camera/microphone permission denied.' :
          err.name === 'NotFoundError'   ? 'No camera or microphone found.' :
                                           'Could not access media devices.'
        setMediaError(msg)
        setConnectionStatus('error')
        return
      }

      if (!mountedRef.current) { stopTracks(stream); return }
      localStreamRef.current = stream
      setLocalStream(stream)

      // ── new Peer() ────────────────────────────────────────────────────────
      peer = new Peer(PEERJS_CONFIG)
      peerRef.current = peer

      peer.on('error', (err) => {
        console.error('[PeerJS] error:', err.type, err.message)
        if (mountedRef.current) setConnectionStatus('error')
      })

      peer.on('open', async (peerId) => {
        if (!mountedRef.current) return
        console.log('[PeerJS] open, peerId:', peerId)

        if (isCreator) {
          // Same as meet: UPDATE rooms SET creator_peer_id = peerId
          const { error } = await supabase
            .from('rooms')
            .update({ creator_peer_id: peerId })
            .eq('id', roomId)
          if (error) console.error('[PeerJS] failed to save creator_peer_id:', error.message)
          else console.log('[PeerJS] creator_peer_id saved to rooms ✓')

        } else {
          // Same as meet's joinRoomLogic: call immediately inside peer.on('open')
          if (creatorPeerId) {
            console.log('[PeerJS] guest calling creator immediately:', creatorPeerId)
            const call = peer.call(creatorPeerId, stream)
            handleCall(call)
          }
          // If creatorPeerId was null, the Realtime listener below will trigger the call
        }

        setupRealtimeFallback(peer, stream, creatorPeerId)
      })

      // Same as meet: creator answers on peer.on('call')
      peer.on('call', (call) => {
        if (!mountedRef.current) return
        console.log('[PeerJS] incoming call — answering')
        call.answer(localStreamRef.current)
        handleCall(call)
      })
    }

    init()

    return () => {
      stopTracks(stream)
      if (peer && !peer.destroyed) peer.destroy()
      peerRef.current = null
      localStreamRef.current = null
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [roomId, isCreator]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Realtime fallback ─────────────────────────────────────────────────────
  // Only fires if guest loaded before creator saved their peer ID.
  // Watches rooms.creator_peer_id — same column the creator writes to.
  // Note: rooms must be in the Supabase realtime publication for this to work.
  // If it isn't, run: ALTER PUBLICATION supabase_realtime ADD TABLE public.rooms;

  function setupRealtimeFallback(peer, stream, alreadyCalledPeerId) {
    if (channelRef.current || isCreator) return

    const channel = supabase.channel(`video-fallback:${roomId}`)
    channel.on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'rooms', filter: `id=eq.${roomId}` },
      (payload) => {
        const newPeerId = payload.new?.creator_peer_id
        if (
          newPeerId &&
          newPeerId !== alreadyCalledPeerId &&
          !currentCallRef.current &&
          peerRef.current &&
          localStreamRef.current
        ) {
          console.log('[PeerJS] got creator_peer_id via Realtime, calling:', newPeerId)
          const call = peerRef.current.call(newPeerId, localStreamRef.current)
          handleCall(call)
          supabase.removeChannel(channel)
          channelRef.current = null
        }
      }
    ).subscribe()

    channelRef.current = channel
  }

  // ── handleCall — same as meet ─────────────────────────────────────────────

  function handleCall(call) {
    if (!call) return
    currentCallRef.current = call

    call.on('stream', (remote) => {
      if (!mountedRef.current) return
      console.log('[PeerJS] remote stream received ✓')
      setRemoteStream(remote)
      setCallConnected(true)
      setConnectionStatus('connected')
    })

    call.on('close', () => {
      if (!mountedRef.current) return
      setRemoteStream(null)
      setCallConnected(false)
      setConnectionStatus('disconnected')
    })

    call.on('error', (err) => {
      console.error('[PeerJS] call error:', err)
      if (mountedRef.current) setConnectionStatus('error')
    })

    call.peerConnection?.addEventListener('iceconnectionstatechange', () => {
      const state = call.peerConnection?.iceConnectionState
      console.log('[PeerJS] ICE:', state)
      if (state === 'failed' && mountedRef.current) {
        setConnectionStatus('error')
        setCallConnected(false)
      }
    })
  }

  // ── controls ──────────────────────────────────────────────────────────────

  const toggleMute = useCallback(() => {
    const s = localStreamRef.current
    if (!s) return
    s.getAudioTracks().forEach((t) => { t.enabled = !t.enabled })
    setIsMuted((p) => !p)
  }, [])

  const toggleCamera = useCallback(() => {
    const s = localStreamRef.current
    if (!s) return
    s.getVideoTracks().forEach((t) => { t.enabled = !t.enabled })
    setIsCameraOff((p) => !p)
  }, [])

  return {
    localStream,
    remoteStream,
    callConnected,
    mediaError,
    isMuted,
    isCameraOff,
    connectionStatus,
    toggleMute,
    toggleCamera,
  }
}

function stopTracks(stream) {
  if (!stream) return
  stream.getTracks().forEach((t) => t.stop())
}